import { getLocalization } from "src/utils";

/**
 * A function that takes in a value of "any" type and returns a currency string in whole number which is formatted per the locale and specified currency
 * @param value - can be any value. Note that it does not parse number from a currency formatted string such as "$1,000".
 * @param options - any additional options from the JS Intl API
 */
export function localizeCurrencyToWholeNumber(value: any, options?: Intl.NumberFormatOptions): string {
    const num: number = parseInt(value, 10) || 0;

    const { locale, currency } = getLocalization();

    try {
        return Intl.NumberFormat(locale, {
            currency,
            currencyDisplay: currency === "CAD" ? "narrowSymbol" : "symbol",
            maximumSignificantDigits: 6,
            minimumSignificantDigits: 1,
            style: "currency",
            ...options,
        })
            .format(num)
            .trim();
    } catch (e) {
        // CAD symbol display changed with node 14 and narrowSymbol now matches our desired behavior, but some older
        // browsers don't like narrowSymbol so fall back to symbol if we catch an error
        return Intl.NumberFormat(locale, {
            currency,
            currencyDisplay: "symbol",
            maximumSignificantDigits: 6,
            minimumSignificantDigits: 1,
            style: "currency",
            ...options,
        })
            .format(num)
            .trim();
    }
}

/**
 * A function that takes in a value of "any" type and returns a currency string with decimals which is formatted per the locale and specified currency
 * @param value - can be any value. Note that it does not parse number from a currency formatted string such as "$1,000".
 * @param options - any additional options from the JS Intl API
 */
export function localizeCurrencyWithDecimals(value: any, options?: Intl.NumberFormatOptions): string {
    const num: number = parseFloat(value) || 0;

    const { locale, currency } = getLocalization();

    try {
        return Intl.NumberFormat(locale, {
            currency,
            currencyDisplay: currency === "CAD" ? "narrowSymbol" : "symbol",
            style: "currency",
            ...options,
        })
            .format(num)
            .trim();
    } catch (e) {
        // CAD symbol display changed with node 14 and narrowSymbol now matches our desired behavior, but some older
        // browsers don't like narrowSymbol so fall back to symbol if we catch an error
        return Intl.NumberFormat(locale, {
            currency,
            currencyDisplay: "symbol",
            style: "currency",
            ...options,
        })
            .format(num)
            .trim();
    }
}

export const parseCurrencyToFloat = (currency: string | number): number => {
    if (!currency && currency !== 0) {
        return 0;
    }
    if (!isNaN(Number(currency))) {
        return currency as number;
    }
    return Number(currency.toString().replace(/[^\d.]/g, ""));
};
